import { InteractionEvent, type VerificationCodeIdentifier } from '@logto/schemas';
import { useCallback, useContext, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import UserInteractionContext from '@/Providers/UserInteractionContextProvider/UserInteractionContext';
import { identifyWithVerificationCode, registerUserWithoutSubmit } from '@/apis/experience';
import { CreateAccountModalContent } from '@/components/CreateAccountModalContent';
import useApi from '@/hooks/use-api';
import { useConfirmModal } from '@/hooks/use-confirm-modal';
import type { ErrorHandlers } from '@/hooks/use-error-handler';
import useErrorHandler from '@/hooks/use-error-handler';
import useGlobalRedirectTo from '@/hooks/use-global-redirect-to';
import { useSieMethods } from '@/hooks/use-sie';
import useSubmitInteractionErrorHandler from '@/hooks/use-submit-interaction-error-handler';
import { codeVerificationTypeMap } from '@/utils/sign-in-experience';
import { otpTimerStorageKey } from '@/utils/otp-timer';

import useGeneralVerificationCodeErrorHandler from './use-general-verification-code-error-handler';
import useIdentifierErrorAlert, { IdentifierErrorType } from './use-identifier-error-alert';

const useSignInFlowCodeVerification = (
  identifier: VerificationCodeIdentifier,
  verificationId: string,
  errorCallback?: () => void
) => {
  const { show } = useConfirmModal();
  const navigate = useNavigate();
  const redirectTo = useGlobalRedirectTo();
  const { isVerificationCodeEnabledForSignUp, passwordRequiredForSignUp } = useSieMethods();
  const handleError = useErrorHandler();
  const registerWithIdentifierAsync = useApi(registerUserWithoutSubmit);
  const asyncSignInWithVerificationCodeIdentifier = useApi(identifyWithVerificationCode);
  const { clearVerificationId } = useContext(UserInteractionContext);

  const { errorMessage, clearErrorMessage, generalVerificationCodeErrorHandlers } =
    useGeneralVerificationCodeErrorHandler();

  const preSignInErrorHandler = useSubmitInteractionErrorHandler(InteractionEvent.SignIn, {
    replace: true,
  });
  const preRegisterErrorHandler = useSubmitInteractionErrorHandler(InteractionEvent.Register, {
    replace: true,
  });

  const showIdentifierErrorAlert = useIdentifierErrorAlert();

  const identifierNotExistErrorHandler = useCallback(async () => {
    const { type, value } = identifier;

    // Should not redirect user to register if is sign-in only mode
    if (!isVerificationCodeEnabledForSignUp(type)) {
      void showIdentifierErrorAlert(IdentifierErrorType.IdentifierNotExist, type, value);

      return;
    }

    show({
      confirmText: 'action.create',
      ModalContent: () => CreateAccountModalContent(type, value),
      onConfirm: async () => {
        const [error] = await registerWithIdentifierAsync(verificationId, type);

        if (error) {
          await handleError(error, preRegisterErrorHandler);

          return;
        }

        // The user is not created yet, only the identifier is staged in the interaction.
        // Collect password (if required) and extra profile first; the extra-profile page
        // creates the user and submits the interaction in one atomic step.
        navigate(passwordRequiredForSignUp ? '/continue/password' : '/continue/extra-profile', {
          replace: true,
          state: { interactionEvent: InteractionEvent.Register, isSocialLogin: false },
        });
      },
      onCancel: () => {
        navigate(-1);
      },
    });
  }, [
    identifier,
    isVerificationCodeEnabledForSignUp,
    passwordRequiredForSignUp,
    show,
    showIdentifierErrorAlert,
    registerWithIdentifierAsync,
    verificationId,
    handleError,
    preRegisterErrorHandler,
    navigate,
  ]);

  const sessionNotFoundHandler = useCallback(() => {
    // Clear the cached verificationId and timer so the next sign-in attempt sends a fresh OTP
    clearVerificationId(codeVerificationTypeMap[identifier.type]);
    sessionStorage.removeItem(otpTimerStorageKey);
    errorCallback?.();
  }, [clearVerificationId, identifier.type, errorCallback]);

  const errorHandlers = useMemo<ErrorHandlers>(
    () => ({
      'user.user_not_exist': identifierNotExistErrorHandler,
      'session.verification_session_not_found': sessionNotFoundHandler,
      ...generalVerificationCodeErrorHandlers,
      ...preSignInErrorHandler,
      callback: errorCallback,
    }),
    [
      errorCallback,
      identifierNotExistErrorHandler,
      sessionNotFoundHandler,
      preSignInErrorHandler,
      generalVerificationCodeErrorHandlers,
    ]
  );

  const onSubmit = useCallback(
    async (code: string) => {
      const [error, result] = await asyncSignInWithVerificationCodeIdentifier({
        verificationId,
        identifier,
        code,
      });

      if (error) {
        await handleError(error, errorHandlers);
        return;
      }

      if (result?.redirectTo) {
        await redirectTo(result.redirectTo);
      }
    },
    [
      asyncSignInWithVerificationCodeIdentifier,
      errorHandlers,
      handleError,
      identifier,
      redirectTo,
      verificationId,
    ]
  );

  return {
    errorMessage,
    clearErrorMessage,
    onSubmit,
  };
};

export default useSignInFlowCodeVerification;
